import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { apiService } from '../services/api';
import type { AppDispatch, RootState } from './index';
import type { ParserProfile, TaskResponse } from '../types/timekeeping';
import type { SelectedUnit } from '../types/auth';

const STORAGE_KEY_PREFIX = 'timekeeping_import_settings_v1';

function settingsKey(userName: string, unit: SelectedUnit): string {
  return `${STORAGE_KEY_PREFIX}_${userName}_${unit.tenant}_${unit.unit}`;
}

const today = new Date().toISOString().slice(0, 10);

interface TimekeepingState {
  profiles: ParserProfile[];
  profilesLoading: boolean;
  selectedProfileId: string;
  tasks: TaskResponse[];
  tasksLoading: boolean;
  folderName: string;
  showPreview: boolean;
  fromDate: string;
  toDate: string;
  submitting: boolean;
  error: string;
}

const initialState: TimekeepingState = {
  profiles: [],
  profilesLoading: false,
  selectedProfileId: '',
  tasks: [],
  tasksLoading: false,
  folderName: '',
  showPreview: false,
  fromDate: today,
  toDate: today,
  submitting: false,
  error: '',
};

const timekeepingSlice = createSlice({
  name: 'timekeeping',
  initialState,
  reducers: {
    setProfiles(state, action: PayloadAction<ParserProfile[]>) {
      state.profiles = action.payload;
    },
    setProfilesLoading(state, action: PayloadAction<boolean>) {
      state.profilesLoading = action.payload;
    },
    setSelectedProfileId(state, action: PayloadAction<string>) {
      state.selectedProfileId = action.payload;
    },
    setTasks(state, action: PayloadAction<TaskResponse[]>) {
      state.tasks = action.payload;
    },
    setTasksLoading(state, action: PayloadAction<boolean>) {
      state.tasksLoading = action.payload;
    },
    setFolderName(state, action: PayloadAction<string>) {
      state.folderName = action.payload;
    },
    setShowPreview(state, action: PayloadAction<boolean>) {
      state.showPreview = action.payload;
    },
    setFromDate(state, action: PayloadAction<string>) {
      state.fromDate = action.payload;
    },
    setToDate(state, action: PayloadAction<string>) {
      state.toDate = action.payload;
    },
    setSubmitting(state, action: PayloadAction<boolean>) {
      state.submitting = action.payload;
    },
    setError(state, action: PayloadAction<string>) {
      state.error = action.payload;
    },
  },
});

export const {
  setProfiles,
  setProfilesLoading,
  setSelectedProfileId,
  setTasks,
  setTasksLoading,
  setFolderName,
  setShowPreview,
  setFromDate,
  setToDate,
  setSubmitting,
  setError,
} = timekeepingSlice.actions;

export function loadParserProfiles(userName: string | null, selectedUnit: SelectedUnit | null) {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    if (getState().timekeeping.profiles.length > 0) return;
    dispatch(setProfilesLoading(true));
    try {
      const res = await apiService.listParserProfiles();
      const loaded: ParserProfile[] = res.data ?? [];
      dispatch(setProfiles(loaded));
      if (userName && selectedUnit) {
        try {
          const raw = localStorage.getItem(settingsKey(userName, selectedUnit));
          if (raw) {
            const saved = JSON.parse(raw);
            if (saved.profileId && loaded.some((p) => p.id === saved.profileId)) {
              dispatch(setSelectedProfileId(saved.profileId));
            }
          }
        } catch { /* ignore corrupt storage */ }
      }
    } catch (e: any) {
      dispatch(setError(e.message || 'Failed to load profiles'));
    } finally {
      dispatch(setProfilesLoading(false));
    }
  };
}

export function fetchTasks() {
  return async (dispatch: AppDispatch): Promise<TaskResponse[]> => {
    dispatch(setTasksLoading(true));
    try {
      const res = await apiService.listTasks('TIMEKEEPING_IMPORT');
      const loaded = res.data ?? [];
      dispatch(setTasks(loaded));
      return loaded;
    } catch (e: any) {
      dispatch(setError(e.message || 'Failed to load tasks'));
      return [];
    } finally {
      dispatch(setTasksLoading(false));
    }
  };
}

export function persistProfileSelection(
  profileId: string,
  userName: string | null,
  selectedUnit: SelectedUnit | null,
) {
  return (dispatch: AppDispatch) => {
    dispatch(setSelectedProfileId(profileId));
    if (userName && selectedUnit) {
      try {
        localStorage.setItem(settingsKey(userName, selectedUnit), JSON.stringify({ profileId }));
      } catch { /* non-fatal */ }
    }
  };
}

export default timekeepingSlice.reducer;
